import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiUrl } from '../../services/api';
import { useI18n } from '../../contexts/I18nContext';
import { localizePath } from '../../utils/localePaths';
import { useScopedUiI18n } from '../../i18n/scopedUiTranslations';

export function Footer() {
  const [notices, setNotices] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('dismissed_notices') || '[]'); } catch { return []; }
  });
  const navigate = useNavigate();
  const { lang } = useI18n();
  const { tr } = useScopedUiI18n();
  const localePath = (path) => localizePath(path, lang);

  useEffect(() => {
    fetch(apiUrl('/notifications/public/notices'))
      .then(r => r.json())
      .then(data => { if (data.success && data.notices?.length) setNotices(data.notices); })
      .catch(() => {});
  }, []);

  const dismiss = (id) => {
    const next = [...dismissed, id];
    setDismissed(next);
    sessionStorage.setItem('dismissed_notices', JSON.stringify(next));
  };

  const visibleNotices = notices.filter(n => !dismissed.includes(n.id));

  return (
    <footer style={{
      borderTop: '1px solid var(--glass-border)',
      margin: '0',
      padding: '32px 16px 24px',
      background: 'var(--glass-bg)',
      backdropFilter: 'blur(32px) saturate(180%)',
      WebkitBackdropFilter: 'blur(32px) saturate(180%)',
      position: 'relative',
    }}>
      {/* Liquid Glass shimmer */}
      <div style={{
        position: 'absolute', left: 0, right: 0, top: 0, height: 1, pointerEvents: 'none',
        background: 'linear-gradient(90deg, transparent, rgba(200,160,255,0.25), transparent)',
      }} />
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        {/* Business info */}
        <div style={{ textAlign: 'center', fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', lineHeight: 2 }}>
          <p style={{ fontFamily: 'var(--p2-display, var(--font-serif))', fontSize: 20, fontWeight: 700, fontStyle: 'italic', color: 'var(--text-primary)', marginBottom: 10 }}>
            {tr('brand')}<em style={{ fontStyle: 'normal', color: 'var(--p2-pink, var(--accent))' }}>·</em>命
          </p>
          <p>{tr('companyInfo')}</p>
          <p>{tr('salesInfo')}</p>
          <p>{tr('safePhone')}</p>
          <p>{tr('copyright')}</p>
          <p style={{ marginTop: 4 }}>{tr('disclaimer')}</p>
          <p style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--separator)' }}>
            <span style={{ fontWeight: 700 }}>{tr('businessInquiry')}</span>
            <span style={{ color: 'var(--text-muted)', margin: '0 6px' }}>|</span>
            <span style={{ color: 'var(--accent)', fontWeight: 500, cursor: 'pointer' }} onClick={() => { const d = atob('bWFzdGVyQHNrdGVjLmty'); window.location.href = 'mai' + 'lto:' + d; }}>{atob('bWFzdGVyQHNrdGVjLmty')}</span>
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 12 }}>
            <Link to={localePath('/privacy')} style={{ color: 'var(--accent)', fontWeight: 500 }}>{tr('privacy')}</Link>
            <span style={{ color: 'var(--text-muted)' }}>|</span>
            <Link to={localePath('/terms')} style={{ color: 'var(--accent)', fontWeight: 500 }}>{tr('terms')}</Link>
            <span style={{ color: 'var(--text-muted)' }}>|</span>
            <Link to={localePath('/refund-policy')} style={{ color: 'var(--accent)', fontWeight: 500 }}>{tr('refund')}</Link>
          </div>
        </div>

        {/* 공지 배너 (푸터 아래) */}
        {visibleNotices.length > 0 && (
          <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {visibleNotices.map(n => (
              <div key={n.id} style={{
                padding: '12px 16px', borderRadius: 'var(--radius-md)',
                background: 'linear-gradient(135deg, rgba(168,85,247,0.12), rgba(236,72,153,0.08))',
                backdropFilter: 'blur(20px) saturate(160%)',
                WebkitBackdropFilter: 'blur(20px) saturate(160%)',
                border: '1px solid rgba(168,85,247,0.3)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1)',
                display: 'flex', alignItems: 'flex-start', gap: 10,
              }}>
                <span style={{ fontSize: 18, lineHeight: 1 }}>📢</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 2 }}>{n.title}</div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', lineHeight: 1.6, whiteSpace: 'pre-line' }}>{n.content}</div>
                  {n.link && (
                    <button onClick={() => n.link.startsWith('http') ? window.open(n.link, '_blank', 'noopener') : navigate(localePath(n.link))}
                      style={{ marginTop: 6, padding: '4px 12px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                        background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.3)', color: '#a855f7', cursor: 'pointer' }}>
                      {tr('noticeDetails')}
                    </button>
                  )}
                </div>
                <button onClick={() => dismiss(n.id)}
                  style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16, padding: 0, lineHeight: 1 }}
                  aria-label={tr('close')}>✕</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </footer>
  );
}
